import React, { useState, useEffect, useMemo } from 'react';
import AnalyticsDashboard from './AnalyticsDashboard';
import './App.css';
import apiService from './services/api';

// Cost Inflation Index (CII) for India
const CII = {
  2001: 100, 2002: 105, 2003: 109, 2004: 113, 2005: 117, 2006: 122, 2007: 129, 2008: 137,
  2009: 148, 2010: 167, 2011: 184, 2012: 200, 2013: 220, 2014: 240, 2015: 254, 2016: 264,
  2017: 272, 2018: 280, 2019: 289, 2020: 301, 2021: 317, 2022: 331, 2023: 348, 2024: 363
};

// Utility functions
function monthsBetween(date1, date2) {
  if (!date1 || !date2) return 0;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;

  const yearDiff = d2.getFullYear() - d1.getFullYear();
  const monthDiff = d2.getMonth() - d1.getMonth();
  return yearDiff * 12 + monthDiff;
}

// NEW: Tax calculation utility functions
function validateTaxDates(acquisitionDate, transferDate) {
  const errors = [];

  if (!acquisitionDate) {
    errors.push('Acquisition Date is required');
  }

  if (!transferDate) {
    errors.push('Transfer Date is required');
  }

  if (acquisitionDate && transferDate) {
    if (new Date(acquisitionDate) >= new Date(transferDate)) {
      errors.push('Acquisition Date must be before Transfer Date');
    }
  }

  return errors;
}

function checkRegimeEligibility(acquisitionDate) {
  if (!acquisitionDate) return { eligible: false, message: 'Acquisition date required' };

  const cutoffDate = new Date('2024-07-23');
  const acquisition = new Date(acquisitionDate);

  if (acquisition >= cutoffDate) {
    return {
      eligible: false,
      allowedRegimes: ['New'],
      message: 'Only New Regime allowed for properties acquired after July 23, 2024'
    };
  } else {
    return {
      eligible: true,
      allowedRegimes: ['Old', 'New'],
      message: 'Both Old and New Regimes available'
    };
  }
}

function validateTaxRegime(acquisitionDate, selectedRegime) {
  const eligibility = checkRegimeEligibility(acquisitionDate);

  if (!eligibility.eligible) {
    return { valid: false, message: eligibility.message };
  }

  if (selectedRegime === 'Old' && !eligibility.allowedRegimes.includes('Old')) {
    return {
      valid: false,
      forcedRegime: 'New',
      message: 'Old Regime not eligible. Forcing New Regime.'
    };
  }

  return { valid: true, regime: selectedRegime };
}

function getYear(date) {
  if (!date) return new Date().getFullYear();
  const d = new Date(date);
  return isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
}

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN');
}

// Category mapping functions
function getPurchaseCategory(description) {
  const categoryMap = {
    'Token Money': 'token_money',
    'Down Payment': 'down_payment',
    '1st Installment': 'first_installment',
    '2nd Installment': 'second_installment',
    '3rd Installment': 'third_installment',
    'Possession Payment': 'possession_payment',
    'Final Payment': 'final_payment',
    'Other Purchase Cost': 'other_purchase_cost'
  };
  return categoryMap[description] || 'other_purchase_cost';
}

function getSaleCategory(description) {
  const categoryMap = {
    'Token': 'token',
    'ATS': 'ats',
    'Other Sale Proceeds': 'other_sale_proceeds',
    'Final Payment': 'final_payment',
    'Others': 'custom_sale_description'
  };
  return categoryMap[description] || 'other_sale_proceeds';
}

function getExpenseCategory(type, description) {
  if (type === 'Income') {
    return 'income';
  }
  const categoryMap = {
    'Stamp Duty': 'stamp_duty',
    'Registration Charges': 'registration_charges',
    'Brokerage': 'brokerage',
    'Legal Charges': 'legal_charges',
    'Maintenance Charges': 'maintenance_charges',
    'Property Tax': 'property_tax',
    'Insurance': 'insurance',
    'Others': 'custom_expense_description'
  };
  return categoryMap[description] || 'custom_expense_description';
}

const tabList = [
  { key: 'purchase', label: 'Purchase Details' },
  { key: 'sale', label: 'Sale Details' },
  { key: 'expenses', label: 'Additional Costs & Income' },
  { key: 'tax', label: 'Tax Settings' },
  { key: 'analytics', label: '📊 Analytics', premium: true },
];

function PurchaseTab({ transactions, setTransactions, onAddTransaction, onRemoveTransaction, user }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('Token Money');
  const [dateError, setDateError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddTransaction = async () => {
    if (!date) {
      setDateError('Please select a date');
      return;
    }
    setDateError('');
    if (!amount) return;

    const transaction = {
      amount: parseFloat(amount),
      date,
      description,
      id: Date.now(),
    };

    setIsSaving(true);
    try {
      if (onAddTransaction) {
        await onAddTransaction(transaction);
      } else {
        setTransactions([...transactions, transaction]);
      }
      // Reset form fields
      setAmount('');
      setDate('');
      setDescription('Token Money');
    } catch (error) {
      console.error('Error adding transaction:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveTransaction = async (id) => {
    try {
      if (onRemoveTransaction) {
        // Call backend deletion FIRST
        await onRemoveTransaction(id, 'purchase');
      }
      // Only update local state AFTER successful backend deletion
      const updatedTransactions = transactions.filter(t => t.id !== id);
      setTransactions(updatedTransactions);
    } catch (error) {
      console.error('Error removing transaction:', error);
      // Don't remove from UI if backend deletion failed
      // Transaction stays in UI, error is handled by parent
    }
  };

  return (
    <div className="tab-content active">
      <h3 className="section-header">Purchase Installments & Initial Costs</h3>
      <div className="installment-section">
        <div className="installment-grid">
          <div className="input-group">
            <label htmlFor="purchase-amount">Amount (₹)</label>
            <input
              type="number"
              id="purchase-amount"
              placeholder="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="input-group">
            <label htmlFor="purchase-date">Date</label>
            <input
              type="date"
              id="purchase-date"
              value={date}
              onChange={e => { setDate(e.target.value); setDateError(''); }}
              disabled={isSaving}
            />
            {dateError && <div style={{ color: 'red', fontSize: '0.9em', marginTop: 2 }}>{dateError}</div>}
          </div>
          <div className="input-group">
            <label htmlFor="purchase-description">Description</label>
            <select
              id="purchase-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={isSaving}
            >
              <option value="Token Money">Token Money</option>
              <option value="Down Payment">Down Payment</option>
              <option value="1st Installment">1st Installment</option>
              <option value="2nd Installment">2nd Installment</option>
              <option value="3rd Installment">3rd Installment</option>
              <option value="Possession Payment">Possession Payment</option>
              <option value="Final Payment">Final Payment</option>
              <option value="Other Purchase Cost">Other Purchase Cost</option>
            </select>
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={handleAddTransaction}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Add Purchase Entry'}
            </button>
          </div>
        </div>
      </div>
      <h4 style={{ margin: '20px 0 10px' }}>Purchase & Acquisition Cost History</h4>
      <div className="history-list" id="purchase-history">
        {transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>No purchase transactions added yet</p>
          </div>
        ) : (
          transactions.map(item => (
            <div className="history-item expense" key={item.id}>
              <div className="history-item-details">
                <div className="history-amount">₹{item.amount.toLocaleString('en-IN')}</div>
                <div className="history-date">📅 {new Date(item.transaction_date || item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                <div className="history-description">{item.description}</div>
              </div>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveTransaction(item.id)}
                disabled={isSaving}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SaleTab({ transactions, setTransactions, onAddTransaction, onRemoveTransaction, user }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('Token');
  const [customDescription, setCustomDescription] = useState('');
  const [dateError, setDateError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddTransaction = async () => {
    if (!date) {
      setDateError('Please select a date');
      return;
    }
    setDateError('');
    if (!amount) return;

    const finalDescription = description === 'Others' ? customDescription : description;
    if (description === 'Others' && !customDescription.trim()) {
      setDescriptionError('Please enter a description');
      return;
    }
    setDescriptionError('');

    const transaction = {
      amount: parseFloat(amount),
      date,
      description: finalDescription,
      id: Date.now(),
    };

    setIsSaving(true);
    try {
      if (onAddTransaction) {
        await onAddTransaction(transaction);
      } else {
        setTransactions([...transactions, transaction]);
      }
      // Reset form fields
      setAmount('');
      setDate('');
      setDescription('Token');
      setCustomDescription('');
    } catch (error) {
      console.error('Error adding transaction:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveTransaction = async (id) => {
    try {
      if (onRemoveTransaction) {
        // Call backend deletion FIRST
        await onRemoveTransaction(id, 'sale');
      }
      // Only update local state AFTER successful backend deletion
      const updatedTransactions = transactions.filter(t => t.id !== id);
      setTransactions(updatedTransactions);
    } catch (error) {
      console.error('Error removing transaction:', error);
      // Don't remove from UI if backend deletion failed
      // Transaction stays in UI, error is handled by parent
    }
  };

  return (
    <div className="tab-content active">
      <h3 className="section-header">Sale Proceeds & Receipts</h3>
      <div className="installment-section">
        <div className="installment-grid">
          <div className="input-group">
            <label htmlFor="sale-amount">Amount (₹)</label>
            <input
              type="number"
              id="sale-amount"
              placeholder="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="input-group">
            <label htmlFor="sale-date">Date</label>
            <input
              type="date"
              id="sale-date"
              value={date}
              onChange={e => { setDate(e.target.value); setDateError(''); }}
              disabled={isSaving}
            />
            {dateError && <div style={{ color: 'red', fontSize: '0.9em', marginTop: 2 }}>{dateError}</div>}
          </div>
          <div className="input-group">
            <label htmlFor="sale-description">Type</label>
            <select
              id="sale-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={isSaving}
            >
              <option value="Token">Token</option>
              <option value="ATS">ATS</option>
              <option value="Other Sale Proceeds">Other Sale Proceeds</option>
              <option value="Final Payment">Final Payment</option>
              <option value="Others">Others</option>
            </select>
          </div>
          {description === 'Others' && (
            <div className="input-group">
              <label htmlFor="custom-sale-description">Description</label>
              <input
                type="text"
                id="custom-sale-description"
                data-testid="custom-sale-description"
                placeholder="Enter description"
                value={customDescription}
                onChange={e => setCustomDescription(e.target.value)}
                disabled={isSaving}
              />
              {descriptionError && <div style={{ color: 'red', fontSize: '0.9em', marginTop: 2 }}>{descriptionError}</div>}
            </div>
          )}
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={handleAddTransaction}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Add Sale Entry'}
            </button>
          </div>
        </div>
      </div>
      <h4 style={{ margin: '20px 0 10px' }}>Sale Proceeds History</h4>
      <div className="history-list" id="sale-history">
        {transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>No sale transactions added yet</p>
          </div>
        ) : (
          transactions.map(item => (
            <div className="history-item income" key={item.id}>
              <div className="history-item-details">
                <div className="history-amount">₹{item.amount.toLocaleString('en-IN')}</div>
                <div className="history-date">📅 {new Date(item.transaction_date || item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                <div className="history-description">{item.description}</div>
              </div>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveTransaction(item.id)}
                disabled={isSaving}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ExpensesTab({ transactions, setTransactions, onAddTransaction, onRemoveTransaction, user }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [transactionType, setTransactionType] = useState('Expense');
  const [type, setType] = useState('Brokerage');
  const [customDescription, setCustomDescription] = useState('');
  const [dateError, setDateError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddEntry = async () => {
    if (!date) {
      setDateError('Please select a date');
      return;
    }
    setDateError('');
    if (!amount) return;

    const finalDescription = type === 'Others' ? customDescription : type;
    if (type === 'Others' && !customDescription.trim()) {
      setDescriptionError('Please enter a description');
      return;
    }
    setDescriptionError('');

    const transaction = {
      amount: parseFloat(amount),
      date,
      transactionType,
      type: finalDescription,
      description: finalDescription,
      id: Date.now(),
    };

    setIsSaving(true);
    try {
      if (onAddTransaction) {
        await onAddTransaction(transaction);
      } else {
        setTransactions([...transactions, transaction]);
      }
      // Reset form fields
      setAmount('');
      setDate('');
      setTransactionType('Expense');
      setType('Brokerage');
      setCustomDescription('');
    } catch (error) {
      console.error('Error adding transaction:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveEntry = async (id) => {
    try {
      if (onRemoveTransaction) {
        // Call backend deletion FIRST
        await onRemoveTransaction(id, 'additional');
      }
      // Only update local state AFTER successful backend deletion
      const updatedTransactions = transactions.filter(t => t.id !== id);
      setTransactions(updatedTransactions);
    } catch (error) {
      console.error('Error removing transaction:', error);
      // Don't remove from UI if backend deletion failed
      // Transaction stays in UI, error is handled by parent
    }
  };

  return (
    <div className="tab-content active">
      <h3 className="section-header">Additional Costs & Income</h3>
      <div className="installment-section">
        <div className="installment-grid">
          <div className="input-group">
            <label htmlFor="addl-transaction-type">Type</label>
            <select
              id="addl-transaction-type"
              value={transactionType}
              onChange={e => setTransactionType(e.target.value)}
              disabled={isSaving}
            >
              <option value="Expense">Expense</option>
              <option value="Income">Income</option>
            </select>
          </div>
          <div className="input-group">
            <label htmlFor="addl-amount">Amount (₹)</label>
            <input
              type="number"
              id="addl-amount"
              placeholder="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="input-group">
            <label htmlFor="addl-date">Date</label>
            <input
              type="date"
              id="addl-date"
              value={date}
              onChange={e => { setDate(e.target.value); setDateError(''); }}
              disabled={isSaving}
            />
            {dateError && <div style={{ color: 'red', fontSize: '0.9em', marginTop: 2 }}>{dateError}</div>}
          </div>
          <div className="input-group">
            <label htmlFor="addl-type">Category</label>
            <select
              id="addl-type"
              value={type}
              onChange={e => setType(e.target.value)}
              disabled={isSaving}
            >
              <option value="Brokerage">Brokerage</option>
              <option value="Stamp Duty">Stamp Duty</option>
              <option value="Registration Charges">Registration Charges</option>
              <option value="Legal Fees">Legal Fees</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Property Tax">Property Tax</option>
              <option value="Rental Income">Rental Income</option>
              <option value="Others">Others</option>
            </select>
          </div>
          {type === 'Others' && (
            <div className="input-group">
              <label htmlFor="custom-addl-description">Description</label>
              <input
                type="text"
                id="custom-addl-description"
                data-testid="custom-addl-description"
                placeholder="Enter description"
                value={customDescription}
                onChange={e => setCustomDescription(e.target.value)}
                disabled={isSaving}
              />
              {descriptionError && <div style={{ color: 'red', fontSize: '0.9em', marginTop: 2 }}>{descriptionError}</div>}
            </div>
          )}
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={handleAddEntry}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Add Entry'}
            </button>
          </div>
        </div>
      </div>
      <h4 style={{ margin: '20px 0 10px' }}>Additional Costs & Income History</h4>
      <div className="history-list" id="addl-history">
        {transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>No additional cost or income entries added yet</p>
          </div>
        ) : (
          transactions.map(item => (
            <div className={`history-item ${item.transactionType === 'Income' ? 'income' : 'expense'}`} key={item.id}>
              <div className="history-item-details">
                <div className="history-amount">
                  {item.transactionType === 'Income' ? '+' : '-'}₹{item.amount.toLocaleString('en-IN')}
                </div>
                <div className="history-date">📅 {new Date(item.transaction_date || item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                <div className="history-description" data-testid="addl-history-description">{item.description}</div>
              </div>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveEntry(item.id)}
                disabled={isSaving}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function TaxTab({
  holdingType, setHoldingType,
  ownerType, setOwnerType,
  taxRegime, setTaxRegime,
  applyIndexation, setApplyIndexation,
  tdsChecked, setTdsChecked,
  tdsPercent, setTdsPercent,
  section54Checked, setSection54Checked,
  section54Amount, setSection54Amount,
  section54Error, setSection54Error,
  tdsError, setTdsError,
  onCalculateTax,
  isCalculating,
  calculationMessage,
  clearCalculationMessage,
  purchaseTransactions,
  saleTransactions,
  addlTransactions,
  showAdvancedOptions,
  setShowAdvancedOptions,
  showTaxPreview,
  setShowTaxPreview,
  // NEW: Critical dates for tax calculation
  acquisitionDate, setAcquisitionDate,
  transferDate, setTransferDate,
  user,
  currentProperty
}) {
  const handleSection54Change = (checked) => {
    setSection54Checked(checked);
    if (!checked) setSection54Amount('');
    clearCalculationMessage();
  };

  const handleTdsChecked = (checked) => {
    setTdsChecked(checked);
    if (!checked) setTdsPercent('1');
    clearCalculationMessage();
  };

  // Guest user view - simplified and disabled
  if (!user) {
    return (
      <div className="tab-content active">
        <h3 className="section-header">Capital Gains Tax Settings (India)</h3>

        {/* Guest User Message */}
        <div style={{
          background: '#f8f9fa',
          padding: '32px',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '2px solid #e9ecef',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h4 style={{
            marginBottom: '16px',
            color: '#495057',
            fontSize: '1.3em',
            fontWeight: '600'
          }}>
            Sign In Required for Tax Calculations
          </h4>
          <p style={{
            color: '#6c757d',
            fontSize: '1.1em',
            lineHeight: '1.6',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Tax calculations require user authentication to access advanced features,
            save your settings, and perform backend calculations.
            Sign in to unlock full tax planning capabilities.
          </p>
        </div>

        {/* Disabled Tax Settings Preview */}
        <div style={{
          background: '#f8f9fa',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{
            marginBottom: '16px',
            color: '#6c757d',
            fontSize: '1.1em',
            fontStyle: 'italic'
          }}>
            Tax Settings Preview (Sign in to edit)
          </h4>

          <div className="tax-section" style={{ opacity: 0.6, pointerEvents: 'none' }}>
            {/* Critical Dates Section */}
            <div className="critical-dates-section" style={{
              background: '#e9ecef',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '2px solid #ced4da'
            }}>
              <h4 style={{ marginBottom: '16px', color: '#6c757d', fontSize: '1.1em' }}>
                🗓️ Critical Dates for Tax Calculation
              </h4>
              <div className="input-row">
                <div className="input-group">
                  <label htmlFor="acquisition-date-disabled">Acquisition Date *</label>
                  <input
                    type="date"
                    id="acquisition-date-disabled"
                    value=""
                    disabled
                    style={{ width: '100%', background: '#f8f9fa' }}
                  />
                  <small style={{ color: '#adb5bd', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                    Allotment date OR possession/registration date, whichever is earlier
                  </small>
                </div>
                <div className="input-group">
                  <label htmlFor="transfer-date-disabled">Transfer Date *</label>
                  <input
                    type="date"
                    id="transfer-date-disabled"
                    value=""
                    disabled
                    style={{ width: '100%', background: '#f8f9fa' }}
                  />
                  <small style={{ color: '#adb5bd', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                    Date when ownership was transferred to buyer
                  </small>
                </div>
              </div>
            </div>

            {/* Tax Settings Section */}
            <div className="tax-section">
              <div className="input-row">
                <div className="input-group">
                  <label htmlFor="holding-type-disabled">Holding Type</label>
                  <select id="holding-type-disabled" value="LTCG" disabled style={{ background: '#f8f9fa' }}>
                    <option value="LTCG">Long-Term Capital Gains (LTCG)</option>
                    <option value="STCG">Short-Term Capital Gains (STCG)</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="owner-type-disabled">Owner Type</label>
                  <select id="owner-type-disabled" value="Individual" disabled style={{ background: '#f8f9fa' }}>
                    <option value="Individual">Individual</option>
                    <option value="HUF">HUF</option>
                    <option value="Company">Company</option>
                    <option value="NRI">NRI</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="tax-regime-disabled">Tax Regime</label>
                  <select id="tax-regime-disabled" value="Old" disabled style={{ background: '#f8f9fa' }}>
                    <option value="Old">Old Regime</option>
                    <option value="New">New Regime</option>
                  </select>
                </div>
              </div>
              <div className="input-row">
                <div className="input-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                    />
                    Apply Indexation (CII)
                  </label>
                </div>
                <div className="input-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                    />
                    TDS Deducted on Sale
                  </label>
                </div>
                <div className="input-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                    />
                    Claim Section 54 Exemption
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits of Signing In */}
        <div style={{
          marginTop: '24px',
          background: '#e3f2fd',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #bbdefb'
        }}>
          <h4 style={{ marginBottom: '16px', color: '#1976d2', fontSize: '1.1em' }}>
            💡 Benefits of Signing In
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            fontSize: '0.95em'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '8px' }}>🔐 Secure Data</div>
              <div style={{ color: '#424242' }}>Save your transactions and tax settings securely</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '8px' }}>📊 Advanced Calculations</div>
              <div style={{ color: '#424242' }}>Access backend tax calculation engine</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '8px' }}>💾 Data Persistence</div>
              <div style={{ color: '#424242' }}>Your data persists across sessions</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '8px' }}>📈 Tax Planning</div>
              <div style={{ color: '#424242' }}>Compare Old vs New tax regimes</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular user view - full functionality (existing code unchanged)
  return (
    <div className="tab-content active">
      <h3 className="section-header">Capital Gains Tax Settings (India)</h3>

      {/* NEW: Critical dates for tax calculation */}
      <div className="critical-dates-section" style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '2px solid #e9ecef'
      }}>
        <h4 style={{ marginBottom: '16px', color: '#495057', fontSize: '1.1em' }}>
          🗓️ Critical Dates for Tax Calculation
        </h4>
        <div className="input-row">
          <div className="input-group">
            <label htmlFor="acquisition-date">Acquisition Date *</label>
            <input
              type="date"
              id="acquisition-date"
              value={acquisitionDate}
              onChange={e => {
                setAcquisitionDate(e.target.value);
                clearCalculationMessage();
              }}
              required
              style={{ width: '100%' }}
            />
            <small style={{ color: '#6c757d', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
              Allotment date OR possession/registration date, whichever is earlier
            </small>
          </div>
          <div className="input-group">
            <label htmlFor="transfer-date">Transfer Date *</label>
            <input
              type="date"
              id="transfer-date"
              value={transferDate}
              onChange={e => {
                setTransferDate(e.target.value);
                clearCalculationMessage();
              }}
              required
              style={{ width: '100%' }}
            />
            <small style={{ color: '#6c757d', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
              Date when ownership was transferred to buyer
            </small>
          </div>
        </div>

        {/* Regime eligibility info */}
        {acquisitionDate && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#e3f2fd',
            borderRadius: '6px',
            fontSize: '0.9em'
          }}>
            <strong>Regime Eligibility:</strong> {
              checkRegimeEligibility(acquisitionDate).message
            }
          </div>
        )}
      </div>

      <div className="tax-section">
        <div className="input-row">
          <div className="input-group">
            <label htmlFor="holding-type">Holding Type</label>
            <select id="holding-type" value={holdingType} onChange={e => {
              setHoldingType(e.target.value);
              setApplyIndexation(e.target.value === 'LTCG');
              clearCalculationMessage();
            }}>
              <option value="LTCG">Long-Term Capital Gains (LTCG)</option>
              <option value="STCG">Short-Term Capital Gains (STCG)</option>
            </select>
          </div>
          <div className="input-group">
            <label htmlFor="owner-type">Owner Type</label>
            <select id="owner-type" value={ownerType} onChange={e => {
              setOwnerType(e.target.value);
              clearCalculationMessage();
            }}>
              <option value="Individual">Individual</option>
              <option value="HUF">HUF</option>
              <option value="Company">Company</option>
              <option value="NRI">NRI</option>
            </select>
          </div>
          <div className="input-group">
            <label htmlFor="tax-regime">Tax Regime</label>
            <select id="tax-regime" value={taxRegime} onChange={e => {
              setTaxRegime(e.target.value);
              clearCalculationMessage();
            }}>
              <option value="Old">Old Regime</option>
              <option value="New">New Regime</option>
            </select>
          </div>
        </div>
        <div className="input-row">
          <div className="input-group">
            <label>
              <input
                type="checkbox"
                checked={applyIndexation}
                onChange={e => {
                  setApplyIndexation(e.target.checked);
                  clearCalculationMessage();
                }}
                disabled={holdingType === 'STCG'}
              />
              Apply Indexation (CII)
            </label>
          </div>
          <div className="input-group">
            <label>
              <input
                type="checkbox"
                checked={tdsChecked}
                onChange={e => handleTdsChecked(e.target.checked)}
              />
              TDS Deducted on Sale
            </label>
            {tdsChecked && (
              <div style={{ marginTop: 6 }}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={tdsPercent}
                  onChange={e => {
                    setTdsPercent(e.target.value);
                    clearCalculationMessage();
                  }}
                  style={{ width: 60, marginRight: 4 }}
                />
                % TDS
                {tdsError && <div style={{ color: 'red', fontSize: '0.9em' }}>{tdsError}</div>}
              </div>
            )}
          </div>
          <div className="input-group">
            <label>
              <input
                type="checkbox"
                checked={section54Checked}
                onChange={e => handleSection54Change(e.target.checked)}
                disabled={currentProperty?.property_type === 'commercial'}
              />
              Claim Section 54 Exemption
              {currentProperty?.property_type === 'commercial' && (
                <span style={{ color: '#dc3545', fontSize: '0.85em', marginLeft: '8px' }}>
                  (Not available for commercial properties)
                </span>
              )}
            </label>
            {section54Checked && (
              <div style={{ marginTop: 6 }}>
                <input
                  type="number"
                  min="0"
                  value={section54Amount}
                  onChange={e => {
                    setSection54Amount(e.target.value);
                    clearCalculationMessage();
                  }}
                  placeholder="Reinvested Amount (₹)"
                  style={{ width: 160 }}
                />
                {section54Error && <div style={{ color: 'red', fontSize: '0.9em' }}>{section54Error}</div>}
              </div>
            )}
          </div>
        </div>
        {/* Transaction Summary */}
        <div style={{ marginTop: 20, background: '#e3f2fd', padding: 16, borderRadius: 8, fontSize: '0.9em' }}>
          <h4 style={{ marginBottom: 12, color: '#1976d2' }}>Transaction Summary</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 'bold', color: '#1976d2' }}>Purchase Transactions</div>
              <div style={{ fontSize: '1.1em', marginTop: 4 }}>
                {purchaseTransactions.length} entries
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: 2 }}>
                Total: ₹{purchaseTransactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString('en-IN')}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold', color: '#1976d2' }}>Sale Transactions</div>
              <div style={{ fontSize: '1.1em', marginTop: 4 }}>
                {saleTransactions.length} entries
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: 2 }}>
                Total: ₹{saleTransactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString('en-IN')}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold', color: '#1976d2' }}>Additional Costs/Income</div>
              <div style={{ fontSize: '1.1em', marginTop: 4 }}>
                {addlTransactions.length} entries
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: 2 }}>
                Net: ₹{(addlTransactions.filter(t => t.transactionType === 'Income').reduce((sum, t) => sum + t.amount, 0) -
                  addlTransactions.filter(t => t.transactionType === 'Expense').reduce((sum, t) => sum + t.amount, 0)).toLocaleString('en-IN')}
              </div>
            </div>
          </div>

          {/* Calculation Status */}
          <div style={{ marginTop: 16, padding: '12px', background: '#fff', borderRadius: 6, border: '1px solid #bbdefb' }}>
            <div style={{ fontSize: '0.9em', color: '#1976d2' }}>
              <strong>Calculation Status:</strong> {
                purchaseTransactions.length === 0 && saleTransactions.length === 0
                  ? 'No transactions to calculate'
                  : purchaseTransactions.length === 0
                    ? 'Add purchase transactions to start calculation'
                    : saleTransactions.length === 0
                      ? 'Add sale transactions to complete calculation'
                      : 'Ready for tax calculation'
              }
            </div>
          </div>
        </div>

        {/* Options Toggle */}
        <div style={{ marginTop: 20, textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            style={{
              background: 'none',
              border: '1px solid #4B3FE4',
              color: '#4B3FE4',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9em'
            }}
          >
            {showAdvancedOptions ? '▼ Hide Advanced Options' : '▶ Show Advanced Options'}
          </button>

          <button
            type="button"
            onClick={() => setShowTaxPreview(!showTaxPreview)}
            style={{
              background: 'none',
              border: '1px solid #28a745',
              color: '#28a745',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9em'
            }}
          >
            {showTaxPreview ? '▼ Hide Tax Preview' : '▶ Show Tax Preview'}
          </button>
        </div>

        {/* Advanced Tax Information */}
        {showAdvancedOptions && (
          <div style={{ marginTop: 20, background: '#f0f8ff', padding: 20, borderRadius: 8, border: '1px solid #b3d9ff' }}>
            <h4 style={{ marginBottom: 16, color: '#1976d2' }}>Advanced Tax Information</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <h5 style={{ marginBottom: 8, color: '#1976d2' }}>LTCG Tax Rates</h5>
                <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
                  <div><strong>Individual/HUF (Old Regime):</strong> 20% with indexation</div>
                  <div><strong>Individual/HUF (New Regime):</strong> 10% without indexation</div>
                  <div><strong>Company/NRI:</strong> 20% with indexation</div>
                  <div><strong>Surcharge:</strong> 10% above ₹50L, 15% above ₹1Cr</div>
                </div>
              </div>
              <div>
                <h5 style={{ marginBottom: 8, color: '#1976d2' }}>STCG Tax Rates</h5>
                <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
                  <div><strong>Old Regime:</strong> Slab rates (0%, 5%, 20%, 30%)</div>
                  <div><strong>New Regime:</strong> Slab rates (0%, 5%, 10%, 15%, 20%, 30%)</div>
                  <div><strong>Company/NRI:</strong> 30% flat rate</div>
                  <div><strong>Education Cess:</strong> 4% on tax + surcharge</div>
                </div>
              </div>
              <div>
                <h5 style={{ marginBottom: 8, color: '#1976d2' }}>TDS & Exemptions</h5>
                <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
                  <div><strong>Resident TDS:</strong> 1% on sale consideration</div>
                  <div><strong>NRI TDS:</strong> 20% on sale consideration</div>
                  <div><strong>Section 54:</strong> Reinvest in residential property</div>
                  <div><strong>Indexation:</strong> Available for LTCG only</div>
                </div>
              </div>
            </div>

            {/* Tax Planning Tips */}
            <div style={{ marginTop: 20, padding: '16px', background: '#fff', borderRadius: 6, border: '1px solid #b3d9ff' }}>
              <h5 style={{ marginBottom: 12, color: '#1976d2' }}>💡 Tax Planning Tips</h5>
              <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
                <div><strong>• Hold for 24+ months:</strong> Qualify for LTCG benefits and indexation</div>
                <div><strong>• Consider New Regime:</strong> Lower rate (10%) but no indexation benefits</div>
                <div><strong>• Section 54:</strong> Reinvest gains in residential property to claim exemption</div>
                <div><strong>• Plan sale timing:</strong> Consider financial year for optimal tax planning</div>
                <div><strong>• Document expenses:</strong> Keep records of all property-related costs</div>
              </div>
            </div>
          </div>
        )}

        {/* Tax Preview Section */}
        {showTaxPreview && (
          <div style={{ marginTop: 20, background: '#f0fff0', padding: 20, borderRadius: 8, border: '1px solid #90ee90' }}>
            <h4 style={{ marginBottom: 16, color: '#28a745' }}>📊 Tax Calculation Preview</h4>
            <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
              {purchaseTransactions.length === 0 || saleTransactions.length === 0 ? (
                <div style={{ color: '#666', fontStyle: 'italic' }}>
                  Add purchase and sale transactions to see tax preview
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
                  <div style={{ background: '#fff', padding: '12px', borderRadius: 6, border: '1px solid #d4edda' }}>
                    <div style={{ fontWeight: 'bold', color: '#28a745', marginBottom: 8 }}>Current Settings</div>
                    <div>Owner: {ownerType}</div>
                    <div>Regime: {taxRegime}</div>
                    <div>Holding: {holdingType}</div>
                    <div>Indexation: {applyIndexation ? 'Yes' : 'No'}</div>
                  </div>

                  <div style={{ background: '#fff', padding: '12px', borderRadius: 6, border: '1px solid #d4edda' }}>
                    <div style={{ fontWeight: 'bold', color: '#28a745', marginBottom: 8 }}>Estimated Tax</div>
                    <div>Base Rate: {holdingType === 'LTCG' ? (taxRegime === 'Old' ? '20%' : '10%') : 'Slab rates'}</div>
                    <div>Indexation: {applyIndexation && holdingType === 'LTCG' ? 'Available' : 'Not Available'}</div>
                    <div>Education Cess: 4%</div>
                    {holdingType === 'LTCG' && (
                      <div>Surcharge: {purchaseTransactions.reduce((sum, t) => sum + t.amount, 0) > 10000000 ? '15%' : purchaseTransactions.reduce((sum, t) => sum + t.amount, 0) > 5000000 ? '10%' : 'None'}</div>
                    )}
                  </div>

                  <div style={{ background: '#fff', padding: '12px', borderRadius: 6, border: '1px solid #d4edda' }}>
                    <div style={{ fontWeight: 'bold', color: '#28a745', marginBottom: 8 }}>Regime Comparison</div>
                    {(ownerType === 'Individual' || ownerType === 'HUF') && holdingType === 'LTCG' ? (
                      <div>
                        <div>Old Regime: 20% + Indexation</div>
                        <div>New Regime: 10% (No Indexation)</div>
                        <div style={{ marginTop: 8, fontSize: '0.8em', color: '#666' }}>
                          💡 New regime may be beneficial for short holding periods
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#666', fontStyle: 'italic' }}>
                        Not applicable for current settings
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="tax-info-box" style={{ marginTop: 20, background: '#f8f9fa', padding: 16, borderRadius: 8, fontSize: '0.95em' }}>
          <b>Info:</b> LTCG is taxed at 20% with indexation, STCG is taxed as per slab. Section 54 exemption applies if you reinvest in another property. TDS is 1% for residents, 20% for NRIs (subject to rules). Please consult a tax advisor for details.
        </div>
        <div style={{ marginTop: 24 }}>
          <button
            className="btn btn-primary"
            onClick={onCalculateTax}
            disabled={isCalculating}
            style={{ minWidth: '120px' }}
          >
            {isCalculating ? 'Calculating...' : 'Calculate Tax'}
          </button>

          {calculationMessage && (
            <div style={{
              marginTop: 16,
              padding: '12px 16px',
              background: '#d4edda',
              color: '#155724',
              borderRadius: 6,
              border: '1px solid #c3e6cb',
              fontSize: '0.9em'
            }}>
              ✅ {calculationMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaxSummary({ results }) {
  if (!results) return null;

  const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;
  const formatPercentage = (rate) => `${(rate * 100).toFixed(1)}%`;

  return (
    <div className="tax-summary-card" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px #0001', padding: 24, marginTop: 32, maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
      <h3 style={{ color: '#4B3FE4', marginBottom: 20, textAlign: 'center' }}>Capital Gains Tax Summary</h3>

      {/* Key Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: 8 }}>
          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: 4 }}>Capital Gain</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: results.capitalGain >= 0 ? '#1a8a34' : '#e44b3f' }}>
            {formatCurrency(results.capitalGain)}
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: 8 }}>
          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: 4 }}>Tax Payable</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e44b3f' }}>
            {formatCurrency(results.taxPayable)}
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: 8 }}>
          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: 4 }}>Holding Period</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4B3FE4' }}>
            {results.holdingPeriod} months
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: 8 }}>
          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: 4 }}>Gain Type</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4B3FE4' }}>
            {results.gainType}
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: 8 }}>
          <h4 style={{ marginBottom: 12, color: '#4B3FE4' }}>Purchase Details</h4>
          <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
            <div><b>Net Sale Proceeds:</b> {formatCurrency(results.netSaleProceeds)}</div>
            <div><b>Indexed Purchase Cost:</b> {formatCurrency(results.indexedPurchaseCost)}</div>
          </div>
        </div>
        <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: 8 }}>
          <h4 style={{ marginBottom: 12, color: '#4B3FE4' }}>Tax Details</h4>
          <div style={{ fontSize: '0.9em', lineHeight: 1.6 }}>
            <div><b>Owner Type:</b> {results.ownerType || 'Individual'}</div>
            <div><b>Tax Regime:</b> {results.taxRegime || 'Old'}</div>
            <div><b>Base Tax Rate:</b> {results.taxRate || '20'}%</div>
            {results.surcharge > 0 && (
              <div><b>Surcharge:</b> {results.surcharge}%</div>
            )}
            <div><b>Education Cess:</b> {formatCurrency(results.educationCess || 0)}</div>
            <div><b>Total Tax:</b> {formatCurrency(results.taxPayable)}</div>
            <div><b>Net Tax Due:</b> {formatCurrency(results.netTaxDue || results.taxPayable)}</div>
          </div>
        </div>
      </div>

      {/* Detailed Calculation Breakdown */}
      <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: 8 }}>
        <h4 style={{ marginBottom: 12, color: '#4B3FE4' }}>Calculation Breakdown</h4>
        <pre style={{ background: '#fff', padding: 16, borderRadius: 6, fontSize: '0.9em', whiteSpace: 'pre-wrap', border: '1px solid #e9ecef', overflow: 'auto', maxHeight: '300px' }}>
          {results.breakdown}
        </pre>
      </div>
    </div>
  );
}

// XIRR utility (Newton-Raphson method)
function xirr(cashflows, guess = 0.1) {
  // Validate input
  if (!Array.isArray(cashflows) || cashflows.length < 2) {
    console.error('XIRR: Invalid cashflows array or insufficient data');
    return null;
  }

  // Validate cashflows structure
  const validCashflows = cashflows.filter(cf => {
    if (!cf || typeof cf.amount !== 'number' || !cf.date) {
      console.error('XIRR: Invalid cashflow structure:', cf);
      return false;
    }
    const date = new Date(cf.date);
    if (isNaN(date.getTime())) {
      console.error('XIRR: Invalid date in cashflow:', cf);
      return false;
    }
    return true;
  });

  if (validCashflows.length < 2) {
    console.error('XIRR: Insufficient valid cashflows after validation');
    return null;
  }

  // Sort by date
  validCashflows.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Check if we have both positive and negative cashflows
  const hasOutflows = validCashflows.some(cf => cf.amount < 0);
  const hasInflows = validCashflows.some(cf => cf.amount > 0);

  if (!hasOutflows || !hasInflows) {
    console.error('XIRR: Need both outflows (negative) and inflows (positive)');
    return null;
  }

  // cashflows: [{amount, date}]
  const maxIter = 100;
  const tol = 1e-6;
  let rate = guess;

  function npv(rate) {
    const t0 = new Date(validCashflows[0].date);
    return validCashflows.reduce((sum, cf) => {
      const dt = (new Date(cf.date) - t0) / (365.25 * 24 * 3600 * 1000);
      return sum + cf.amount / Math.pow(1 + rate, dt);
    }, 0);
  }

  try {
    for (let i = 0; i < maxIter; i++) {
      let f = npv(rate);
      let f1 = npv(rate + tol) - f;

      // Prevent division by zero
      if (Math.abs(f1) < 1e-10) {
        console.warn('XIRR: Derivative too small, calculation may be unstable');
        break;
      }

      let newRate = rate - f / f1 * tol;

      // Check for convergence
      if (Math.abs(newRate - rate) < tol) {
        console.log('XIRR: Converged after', i + 1, 'iterations');
        return newRate;
      }

      // Check for reasonable bounds - allow higher rates for real estate investments
      if (newRate < -0.99 || newRate > 50) {
        console.warn('XIRR: Rate out of reasonable bounds:', newRate);
        return null;
      }

      rate = newRate;
    }

    console.warn('XIRR: Did not converge after', maxIter, 'iterations');
    return null;
  } catch (error) {
    console.error('XIRR: Calculation error:', error);
    return null;
  }
}

const Tabs = ({ onOverallResultsChange, user, subscription, onUpgradeClick, currentProperty }) => {
  // State management
  const [purchaseTransactions, setPurchaseTransactions] = useState([]);
  const [saleTransactions, setSaleTransactions] = useState([]);
  const [addlTransactions, setAddlTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('purchase');
  const [results, setResults] = useState(null);

  // Tax tab states
  const [holdingType, setHoldingType] = useState('auto');
  const [ownerType, setOwnerType] = useState('Individual');
  const [taxRegime, setTaxRegime] = useState('Old');
  const [applyIndexation, setApplyIndexation] = useState(true);
  const [tdsChecked, setTdsChecked] = useState(false);
  const [tdsPercent, setTdsPercent] = useState('1');
  const [section54Checked, setSection54Checked] = useState(false);
  const [section54Amount, setSection54Amount] = useState('');
  const [section54Error, setSection54Error] = useState('');
  const [tdsError, setTdsError] = useState('');
  const [calculationMessage, setCalculationMessage] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showTaxPreview, setShowTaxPreview] = useState(false);

  // NEW: Critical dates for tax calculation
  const [acquisitionDate, setAcquisitionDate] = useState('');
  const [transferDate, setTransferDate] = useState('');

  // Legacy fields (can be removed later)
  const [purchasePrice, setPurchasePrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [improvementCost, setImprovementCost] = useState('');
  const [sellingExpenses, setSellingExpenses] = useState('');
  const [exemptionClaimed, setExemptionClaimed] = useState(false);
  const [exemptionSection, setExemptionSection] = useState('');
  const [exemptionAmount, setExemptionAmount] = useState('');

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  // const [currentProperty, setCurrentProperty] = useState(null); // This line was already present, but the user's diff removes it. Assuming the prop `currentProperty` is used instead of local state.

  // Load data when user logs in or currentProperty changes
  useEffect(() => {
    console.log('🔄🔄🔄 useEffect TRIGGERED! currentProperty:', currentProperty?.name, 'ID:', currentProperty?.id, 'user:', !!user);

    const loadDataForProperty = async () => {
      if (!user || !currentProperty) {
        console.log('❌ Clearing data - user:', !!user, 'currentProperty:', !!currentProperty);
        // Clear all data when user logs out or no property selected
        setPurchaseTransactions([]);
        setSaleTransactions([]);
        setAddlTransactions([]);
        setAcquisitionDate('');
        setTransferDate('');
        setResults({
          capitalGain: 0,
          taxPayable: 0,
          netSaleProceeds: 0,
          indexedPurchaseCost: 0,
          holdingPeriod: 0,
          gainType: '-',
          breakdown: currentProperty ? 'Please sign in to calculate tax.' : 'Please select a property to begin.'
        });
        if (!user) {
          setCalculationMessage('👋 Signed out. All data cleared.');
        }
        return;
      }

      // Load data for the current property
      try {
        setIsLoading(true);
        console.log('✅✅✅ Loading data for property:', currentProperty.name, 'ID:', currentProperty.id);

        // Set current property ID in API service
        apiService.setCurrentPropertyId(currentProperty.id);
        console.log('✅ API service property ID set to:', currentProperty.id);

        // Set acquisition date from property purchase_date
        if (currentProperty.purchase_date) {
          setAcquisitionDate(currentProperty.purchase_date);
          console.log('✅ Acquisition date set to:', currentProperty.purchase_date);
        }

        // Load transactions for THIS specific property
        console.log('🔄 Calling apiService.getTransactions with propertyId:', currentProperty.id);
        const response = await apiService.getTransactions(currentProperty.id);
        console.log('✅ API response received:', response);
        const transactions = response.results || [];
        console.log('✅ Total transactions loaded:', transactions.length);

        // Separate transactions by type
        const purchase = transactions.filter(t => t.transaction_type === 'payment');
        const sale = transactions.filter(t => t.transaction_type === 'receipt');
        const additional = transactions.filter(t => t.transaction_type === 'cost');

        console.log('✅ Transactions separated - Purchase:', purchase.length, 'Sale:', sale.length, 'Additional:', additional.length);

        setPurchaseTransactions(purchase);
        setSaleTransactions(sale);
        setAddlTransactions(additional);

        console.log('✅ State updated with new transactions');

        // Set transfer date from latest sale transaction
        if (sale.length > 0) {
          const latestSaleDate = sale.reduce((latest, transaction) => {
            const transactionDate = new Date(transaction.transaction_date);
            const latestDate = new Date(latest);
            return transactionDate > latestDate ? transaction.transaction_date : latest;
          }, sale[0].transaction_date);
          setTransferDate(latestSaleDate);
          console.log('✅ Transfer date set to:', latestSaleDate);
        }

        setCalculationMessage(`✅ Loaded ${purchase.length} purchase and ${sale.length} sale transactions for ${currentProperty.name}`);
        console.log('✅✅✅ Data loading complete for property:', currentProperty.name);
      } catch (error) {
        console.error('❌❌❌ Error loading property data:', error);
        setCalculationMessage('❌ Failed to load property data.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDataForProperty();
  }, [user, currentProperty]);

  // NEW: Auto-select regime when acquisition date changes
  useEffect(() => {
    if (acquisitionDate) {
      const eligibility = checkRegimeEligibility(acquisitionDate);
      if (!eligibility.allowedRegimes.includes(taxRegime)) {
        // Auto-select the first available regime
        setTaxRegime(eligibility.allowedRegimes[0]);
        setCalculationMessage(`Regime automatically updated to ${eligibility.allowedRegimes[0]} based on acquisition date.`);
      }
    }
  }, [acquisitionDate, taxRegime]);

  // NEW: Auto-calculate tax when both dates and transactions are available
  useEffect(() => {
    if (acquisitionDate && transferDate && user &&
      purchaseTransactions.length > 0 && saleTransactions.length > 0) {
      // Show message that auto-calculation is happening
      setCalculationMessage('Auto-calculating tax based on available data...');

      // Auto-calculate tax after a short delay to avoid too many API calls
      const timer = setTimeout(() => {
        calculateTaxAndReturns();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [acquisitionDate, transferDate, purchaseTransactions.length, saleTransactions.length]);

  // Handle date changes and clear results
  useEffect(() => {
    if (acquisitionDate || transferDate) {
      // Clear results when dates change
      setResults({
        capitalGain: 0,
        taxPayable: 0,
        netSaleProceeds: 0,
        indexedPurchaseCost: 0,
        holdingPeriod: 0,
        gainType: '-',
        breakdown: 'Please click "Calculate Tax" to see updated results.'
      });

      // Show message that date was changed
      if (acquisitionDate && transferDate) {
        setCalculationMessage('Dates updated. Tax will be recalculated automatically.');
      } else if (acquisitionDate) {
        setCalculationMessage('Acquisition date updated. Please set transfer date to continue.');
      } else if (transferDate) {
        setCalculationMessage('Transfer date updated. Please set acquisition date to continue.');
      }
    }
  }, [acquisitionDate, transferDate]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clear any timers or async operations
      setResults({
        capitalGain: 0,
        taxPayable: 0,
        netSaleProceeds: 0,
        indexedPurchaseCost: 0,
        holdingPeriod: 0,
        gainType: '-',
        breakdown: ''
      });
      setCalculationMessage('🔄 Component unmounting...');
    };
  }, []);

  const loadUserData = async () => {
    try {
      setIsLoading(true);

      // Use the currentProperty passed from App.js
      if (!currentProperty) {
        console.log('No current property selected');
        setCalculationMessage('Please select a property to begin.');
        return;
      }

      // Set current property ID in API service
      if (currentProperty && currentProperty.id) {
        apiService.setCurrentPropertyId(currentProperty.id);
      }

      // Set acquisition date from property purchase_date
      if (currentProperty && currentProperty.purchase_date) {
        setAcquisitionDate(currentProperty.purchase_date);
        setCalculationMessage('Acquisition date automatically set from property purchase date.');
      }

      // Load transactions for this property
      await loadTransactions();

      // Show success message
      setCalculationMessage('✅ User data loaded successfully. Tax calculation will happen automatically.');

    } catch (error) {
      console.error('Error loading user data:', error);
      setCalculationMessage('❌ Failed to load user data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await apiService.getTransactions();
      const transactions = response.results || [];

      // Separate transactions by type
      const purchase = transactions.filter(t => t.transaction_type === 'payment');
      const sale = transactions.filter(t => t.transaction_type === 'receipt');
      const additional = transactions.filter(t => t.transaction_type === 'cost');

      setPurchaseTransactions(purchase);
      setSaleTransactions(sale);
      setAddlTransactions(additional);

      // Set transfer date from latest sale transaction
      if (sale.length > 0) {
        const latestSaleDate = sale.reduce((latest, transaction) => {
          const transactionDate = new Date(transaction.transaction_date);
          const latestDate = new Date(latest);
          return transactionDate > latestDate ? transaction.transaction_date : latest;
        }, sale[0].transaction_date);

        setTransferDate(latestSaleDate);
        setCalculationMessage(prev => prev ? prev + ' Transfer date automatically set from latest sale transaction.' : 'Transfer date automatically set from latest sale transaction.');
      }

      // Show transaction summary
      const totalPurchase = purchase.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
      const totalSale = sale.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
      setCalculationMessage(prev => prev + ` Loaded ${purchase.length} purchase and ${sale.length} sale transactions. Total: Purchase ₹${totalPurchase.toLocaleString('en-IN')}, Sale ₹${totalSale.toLocaleString('en-IN')}.`);

    } catch (error) {
      console.error('Error loading transactions:', error);
      setCalculationMessage(prev => prev + ' ❌ Failed to load transactions.');
    }
  };

  const saveTransaction = async (transactionData) => {
    if (!user) {
      // For non-logged users, just add to local state
      return null;
    }

    try {
      const savedTransaction = await apiService.createTransaction(transactionData);
      return savedTransaction;
    } catch (error) {
      console.error('Error saving transaction:', error);
      throw error;
    }
  };

  const addPurchaseTransaction = async (transaction) => {
    try {
      if (user) {
        // Save to backend
        const savedTransaction = await saveTransaction({
          transaction_type: 'payment',
          category: getPurchaseCategory(transaction.description),
          amount: transaction.amount,
          transaction_date: transaction.date,
          description: transaction.description
        });

        if (savedTransaction) {
          setPurchaseTransactions(prev => [...prev, savedTransaction]);
        }
      } else {
        // For non-logged users, add to local state
        const newTransaction = {
          id: Date.now(),
          ...transaction,
          created_at: new Date().toISOString()
        };
        setPurchaseTransactions(prev => [...prev, newTransaction]);
      }

      // Update acquisition date if this is the earliest purchase transaction
      const newTransactionDate = new Date(transaction.date);
      let wasDateUpdated = false;
      setAcquisitionDate(prevDate => {
        if (!prevDate || newTransactionDate < new Date(prevDate)) {
          wasDateUpdated = true;
          return transaction.date;
        }
        return prevDate;
      });

      // Clear results when transactions change
      setResults({
        capitalGain: 0,
        taxPayable: 0,
        netSaleProceeds: 0,
        indexedPurchaseCost: 0,
        holdingPeriod: 0,
        gainType: '-',
        breakdown: 'Please wait for automatic calculation...'
      });

      // Show message that transaction was added
      let message = 'Purchase transaction added. Tax will be recalculated automatically.';
      if (wasDateUpdated) {
        message += ' Acquisition date updated automatically.';
      }
      setCalculationMessage(message);

      calculateOverallResults();
      // Clear any previous calculation message when new transactions are added
      setCalculationMessage('');
    } catch (error) {
      console.error('Error adding purchase transaction:', error);
      alert('Failed to save transaction. Please try again.');
    }
  };

  const addSaleTransaction = async (transaction) => {
    try {
      if (user) {
        // Save to backend
        const savedTransaction = await saveTransaction({
          transaction_type: 'receipt',
          category: getSaleCategory(transaction.description === 'Others' ? 'Others' : transaction.description),
          amount: transaction.amount,
          transaction_date: transaction.date,
          description: transaction.description === 'Others' ? transaction.customDescription : transaction.description
        });

        if (savedTransaction) {
          setSaleTransactions(prev => [...prev, savedTransaction]);
        }
      } else {
        // For non-logged users, add to local state
        const newTransaction = {
          id: Date.now(),
          ...transaction,
          created_at: new Date().toISOString()
        };
        setSaleTransactions(prev => [...prev, newTransaction]);
      }

      // Update transfer date if this is the latest sale transaction
      const newTransactionDate = new Date(transaction.date);
      let wasDateUpdated = false;
      setTransferDate(prevDate => {
        if (!prevDate || newTransactionDate > new Date(prevDate)) {
          wasDateUpdated = true;
          return transaction.date;
        }
        return prevDate;
      });

      // Clear results when transactions change
      setResults({
        capitalGain: 0,
        taxPayable: 0,
        netSaleProceeds: 0,
        indexedPurchaseCost: 0,
        holdingPeriod: 0,
        gainType: '-',
        breakdown: 'Please wait for automatic calculation...'
      });

      // Show message that transaction was added
      let message = 'Sale transaction added. Tax will be recalculated automatically.';
      if (wasDateUpdated) {
        message += ' Transfer date updated automatically.';
      }
      setCalculationMessage(message);

      calculateOverallResults();
      // Clear any previous calculation message when new transactions are added
      setCalculationMessage('');
    } catch (error) {
      console.error('Error adding sale transaction:', error);
      alert('Failed to save transaction. Please try again.');
    }
  };

  const addAdditionalTransaction = async (transaction) => {
    try {
      if (user) {
        // Save to backend
        const savedTransaction = await saveTransaction({
          transaction_type: 'cost',
          category: getExpenseCategory(transaction.type, transaction.description),
          amount: transaction.amount,
          transaction_date: transaction.date,
          description: transaction.description || transaction.type
        });

        if (savedTransaction) {
          setAddlTransactions(prev => [...prev, savedTransaction]);
        }
      } else {
        // For non-logged users, add to local state
        const newTransaction = {
          id: Date.now(),
          ...transaction,
          created_at: new Date().toISOString()
        };
        setAddlTransactions(prev => [...prev, newTransaction]);
      }

      // Clear results when transactions change
      setResults({
        capitalGain: 0,
        taxPayable: 0,
        netSaleProceeds: 0,
        indexedPurchaseCost: 0,
        holdingPeriod: 0,
        gainType: '-',
        breakdown: 'Please wait for automatic calculation...'
      });

      // Show message that transaction was added
      setCalculationMessage('Additional transaction added. Tax will be recalculated automatically.');

      calculateOverallResults();
      // Clear any previous calculation message when new transactions are added
      setCalculationMessage('');
    } catch (error) {
      console.error('Error adding additional transaction:', error);
    }
  };

  const removeTransaction = async (transactionId, transactionType) => {
    try {
      if (user) {
        // Delete from backend FIRST
        console.log('Deleting transaction from backend:', transactionId);
        await apiService.deleteTransaction(transactionId);
        console.log('Backend deletion successful');
      }

      // Only remove from local state AFTER successful backend deletion
      console.log('Updating frontend state for transaction type:', transactionType);
      switch (transactionType) {
        case 'purchase':
          setPurchaseTransactions(prev => {
            const filtered = prev.filter(t => t.id !== transactionId);
            console.log('Purchase transactions after removal:', filtered);

            // Recalculate acquisition date from remaining purchase transactions
            let dateMessage = '';
            if (filtered.length > 0) {
              const earliestPurchaseDate = filtered.reduce((earliest, transaction) => {
                const transactionDate = new Date(transaction.transaction_date);
                const earliestDate = new Date(earliest);
                return transactionDate < earliestDate ? transaction.transaction_date : earliest;
              }, filtered[0].transaction_date);

              setAcquisitionDate(earliestPurchaseDate);
              dateMessage = ' Acquisition date recalculated automatically.';
            } else {
              // No purchase transactions left, clear acquisition date
              setAcquisitionDate('');
              dateMessage = ' Acquisition date cleared (no purchase transactions).';
            }

            // Clear results when transactions change
            setResults({
              capitalGain: 0,
              taxPayable: 0,
              netSaleProceeds: 0,
              indexedPurchaseCost: 0,
              holdingPeriod: 0,
              gainType: '-',
              breakdown: 'Please wait for automatic calculation...'
            });

            // Show message that transaction was removed
            setCalculationMessage('Purchase transaction removed.' + dateMessage + ' Tax will be recalculated automatically.');

            // Calculate results with NEW state
            setTimeout(() => calculateOverallResults(), 0);
            return filtered;
          });
          break;
        case 'sale':
          setSaleTransactions(prev => {
            const filtered = prev.filter(t => t.id !== transactionId);
            console.log('Sale transactions after removal:', filtered);

            // Recalculate transfer date from remaining sale transactions
            let dateMessage = '';
            if (filtered.length > 0) {
              const latestSaleDate = filtered.reduce((latest, transaction) => {
                const transactionDate = new Date(transaction.transaction_date);
                const latestDate = new Date(latest);
                return transactionDate > latestDate ? transaction.transaction_date : latest;
              }, filtered[0].transaction_date);

              setTransferDate(latestSaleDate);
              dateMessage = ' Transfer date recalculated automatically.';
            } else {
              // No sale transactions left, clear transfer date
              setTransferDate('');
              dateMessage = ' Transfer date cleared (no sale transactions).';
            }

            // Clear results when transactions change
            setResults({
              capitalGain: 0,
              taxPayable: 0,
              netSaleProceeds: 0,
              indexedPurchaseCost: 0,
              holdingPeriod: 0,
              gainType: '-',
              breakdown: 'Please wait for automatic calculation...'
            });

            // Show message that transaction was removed
            setCalculationMessage('Sale transaction removed.' + dateMessage + ' Tax will be recalculated automatically.');

            // Calculate results with NEW state
            setTimeout(() => calculateOverallResults(), 0);
            return filtered;
          });
          break;
        case 'additional':
          setAddlTransactions(prev => {
            const filtered = prev.filter(t => t.id !== transactionId);
            console.log('Additional transactions after removal:', filtered);

            // Clear results when transactions change
            setResults({
              capitalGain: 0,
              taxPayable: 0,
              netSaleProceeds: 0,
              indexedPurchaseCost: 0,
              holdingPeriod: 0,
              gainType: '-',
              breakdown: 'Please wait for automatic calculation...'
            });

            // Show message that transaction was removed
            setCalculationMessage('Additional transaction removed. Tax will be recalculated automatically.');

            // Calculate results with NEW state
            setTimeout(() => calculateOverallResults(), 0);
            return filtered;
          });
          break;
        default:
          break;
      }

      console.log('Transaction removal completed successfully');
    } catch (error) {
      console.error('Error removing transaction:', error);
      // Don't remove from UI if backend deletion failed
      alert('Failed to remove transaction. Please try again.');
    }
  };

  const calculateOverallResults = () => {
    try {
      // NEW: Validate critical dates first
      const dateErrors = validateTaxDates(acquisitionDate, transferDate);
      if (dateErrors.length > 0) {
        setResults({
          capitalGain: 0,
          taxPayable: 0,
          netSaleProceeds: 0,
          indexedPurchaseCost: 0,
          holdingPeriod: 0,
          gainType: '-',
          breakdown: `Date Validation Errors:\n${dateErrors.join('\n')}\n\nPlease fix these dates to calculate tax.`
        });
        return;
      }

      if (!purchaseTransactions.length || !saleTransactions.length) {
        setResults({
          capitalGain: 0,
          taxPayable: 0,
          netSaleProceeds: 0,
          indexedPurchaseCost: 0,
          holdingPeriod: 0,
          gainType: '-',
          breakdown: 'Please enter at least one purchase and one sale transaction.'
        });
        return;
      }

      // NEW: Validate tax regime selection
      const regimeValidation = validateTaxRegime(acquisitionDate, taxRegime);
      if (!regimeValidation.valid) {
        // Force regime change if needed
        if (regimeValidation.forcedRegime) {
          setTaxRegime(regimeValidation.forcedRegime);
        }
        setResults({
          capitalGain: 0,
          taxPayable: 0,
          netSaleProceeds: 0,
          indexedPurchaseCost: 0,
          holdingPeriod: 0,
          gainType: '-',
          breakdown: `Regime Validation Error:\n${regimeValidation.message}\n\nRegime has been updated to ${regimeValidation.forcedRegime || taxRegime}.`
        });
        return;
      }

      // 1. Aggregate purchase and sale
      const totalPurchase = purchaseTransactions.reduce((sum, t) => sum + t.amount, 0);
      const totalSale = saleTransactions.reduce((sum, t) => sum + t.amount, 0);

      // NEW: Use critical dates instead of transaction dates
      // const purchaseDates = purchaseTransactions.map(t => t.date).sort();
      // const saleDates = saleTransactions.map(t => t.date).sort();
      // const firstPurchaseDate = purchaseDates[0];
      // const lastSaleDate = saleDates[saleDates.length - 1];

      // 2. Additional costs/incomes
      const addlExpenses = addlTransactions.filter(t => t.transactionType === 'Expense').reduce((sum, t) => sum + t.amount, 0);
      const addlIncomes = addlTransactions.filter(t => t.transactionType === 'Income').reduce((sum, t) => sum + t.amount, 0);
      // 3. Net purchase cost
      const netPurchaseCost = totalPurchase + addlExpenses;
      // 4. Net sale proceeds
      const netSaleProceeds = totalSale + addlIncomes;
      // 5. Holding period - NEW: Use critical dates
      const holdingPeriod = monthsBetween(acquisitionDate, transferDate);
      // 6. Gain type - NEW: Auto-determine based on critical dates
      let gainType = holdingType;
      if (holdingType === 'auto') {
        gainType = holdingPeriod >= 24 ? 'LTCG' : 'STCG';
      }
      // 7. Indexation - NEW: Use critical dates for CII calculation
      let indexedPurchaseCost = netPurchaseCost;
      let indexationInfo = '';
      if (gainType === 'LTCG' && applyIndexation && taxRegime === 'Old') {
        const acquisitionYear = getYear(acquisitionDate);
        const transferYear = getYear(transferDate);
        const ciiAcquisition = CII[acquisitionYear] || CII[Object.keys(CII)[0]];
        const ciiTransfer = CII[transferYear] || CII[Object.keys(CII)[Object.keys(CII).length - 1]];
        indexedPurchaseCost = Math.round(netPurchaseCost * (ciiTransfer / ciiAcquisition));
        indexationInfo = `Indexed using CII: Acquisition Year ${acquisitionYear} (${ciiAcquisition}), Transfer Year ${transferYear} (${ciiTransfer})`;
      }
      // 8. Capital gain
      let capitalGain = netSaleProceeds - indexedPurchaseCost;
      // 9. Section 54 exemption
      let section54Exemption = 0;
      if (section54Checked && section54Amount) {
        section54Exemption = Math.min(capitalGain, parseFloat(section54Amount));
        capitalGain -= section54Exemption;
      }
      // 10. Tax calculation
      let taxPayable = 0;
      let taxRate = 0;
      let surcharge = 0;
      let educationCess = 0;

      if (gainType === 'LTCG') {
        // LTCG tax rate based on owner type and tax regime
        if (ownerType === 'Individual' || ownerType === 'HUF') {
          if (taxRegime === 'Old') {
            taxRate = 0.2; // 20% for Old Regime
          } else {
            taxRate = 0.1; // 10% for New Regime (without indexation)
          }
        } else if (ownerType === 'Company') {
          taxRate = 0.2; // 20% for companies
        } else if (ownerType === 'NRI') {
          taxRate = 0.2; // 20% for NRIs
        }

        // Apply surcharge for high-value gains
        if (capitalGain > 10000000) { // Above 1 crore
          surcharge = 0.15; // 15% surcharge
        } else if (capitalGain > 5000000) { // Above 50 lakhs
          surcharge = 0.1; // 10% surcharge
        }

        taxPayable = Math.max(0, Math.round(capitalGain * taxRate));
        if (surcharge > 0) {
          taxPayable += Math.round(taxPayable * surcharge);
        }
      } else {
        // STCG calculation - assume highest tax bracket for real estate investors
        // STCG is added to income and taxed at applicable slab rate
        // For real estate investors, we assume 30% (highest bracket)
        if (ownerType === 'Individual' || ownerType === 'HUF') {
          taxRate = 0.3; // 30% - highest tax bracket assumption
        } else if (ownerType === 'Company') {
          taxRate = 0.3; // 30% for companies
        } else if (ownerType === 'NRI') {
          taxRate = 0.3; // 30% for NRIs
        }

        taxPayable = Math.max(0, Math.round(capitalGain * taxRate));
      }

      // Apply education cess (4% on tax + surcharge)
      educationCess = Math.round(taxPayable * 0.04);
      taxPayable += educationCess;
      // 11. TDS
      let tdsDeducted = 0;
      if (tdsChecked && tdsPercent) {
        tdsDeducted = Math.round(netSaleProceeds * (parseFloat(tdsPercent) / 100));
      }
      // 12. Breakdown
      const breakdown = `CRITICAL DATES:
Acquisition Date: ${acquisitionDate}
Transfer Date: ${transferDate}
Holding Period: ${holdingPeriod} months

TRANSACTION SUMMARY:
Purchase Cost: ₹${totalPurchase.toLocaleString('en-IN')}
Additional Expenses: ₹${addlExpenses.toLocaleString('en-IN')}
Net Purchase Cost: ₹${netPurchaseCost.toLocaleString('en-IN')}
${indexationInfo ? indexationInfo + '\n' : ''}Indexed Purchase Cost: ₹${indexedPurchaseCost.toLocaleString('en-IN')}
Sale Proceeds: ₹${totalSale.toLocaleString('en-IN')}
Additional Incomes: ₹${addlIncomes.toLocaleString('en-IN')}
Net Sale Proceeds: ₹${netSaleProceeds.toLocaleString('en-IN')}

CAPITAL GAINS CALCULATION:
Capital Gain (before exemption): ₹${(netSaleProceeds - indexedPurchaseCost + section54Exemption).toLocaleString('en-IN')}
Section 54 Exemption: ₹${section54Exemption.toLocaleString('en-IN')}
Taxable Capital Gain: ₹${capitalGain.toLocaleString('en-IN')}

TAX CALCULATION DETAILS:
Owner Type: ${ownerType}
Tax Regime: ${taxRegime}
Gain Type: ${gainType}
Base Tax Rate: ${(taxRate * 100).toFixed(1)}%
${surcharge > 0 ? `Surcharge (${(surcharge * 100).toFixed(0)}%): ₹${Math.round(capitalGain * taxRate * surcharge).toLocaleString('en-IN')}` : ''}
Education Cess (4%): ₹${educationCess.toLocaleString('en-IN')}
Total Tax Payable: ₹${taxPayable.toLocaleString('en-IN')}
TDS Deducted: ₹${tdsDeducted.toLocaleString('en-IN')}
Net Tax Due: ₹${Math.max(0, taxPayable - tdsDeducted).toLocaleString('en-IN')}`;
      setResults({
        capitalGain,
        taxPayable,
        netSaleProceeds,
        indexedPurchaseCost,
        holdingPeriod,
        gainType,
        breakdown,
        taxRate: (taxRate * 100).toFixed(1),
        surcharge: surcharge > 0 ? (surcharge * 100).toFixed(0) : 0,
        educationCess,
        netTaxDue: Math.max(0, taxPayable - tdsDeducted),
        ownerType,
        taxRegime
      });

      // Set success message
      setCalculationMessage(`Tax calculation completed successfully! Capital Gain: ${capitalGain >= 0 ? '₹' + capitalGain.toLocaleString('en-IN') : '₹' + Math.abs(capitalGain).toLocaleString('en-IN')} ${capitalGain >= 0 ? 'profit' : 'loss'}`);
    } catch (error) {
      console.error('Error in calculateOverallResults:', error);
      // Set default results on error
      setResults({
        capitalGain: 0,
        taxPayable: 0,
        netSaleProceeds: 0,
        indexedPurchaseCost: 0,
        holdingPeriod: 0,
        gainType: '-',
        breakdown: 'Error calculating results. Please check your data.'
      });
    }
  }

  // Function to calculate tax and returns (called from TaxTab)
  const calculateRegimeComparison = () => {
    if (!purchaseTransactions.length || !saleTransactions.length) {
      return { oldRegime: 0, newRegime: 0, savings: 0 };
    }

    // NEW: Validate dates first
    const dateErrors = validateTaxDates(acquisitionDate, transferDate);
    if (dateErrors.length > 0) {
      return { oldRegime: 0, newRegime: 0, savings: 0, error: 'Please enter both acquisition and transfer dates' };
    }

    const totalPurchase = purchaseTransactions.reduce((sum, t) => sum + t.amount, 0);
    const totalSale = saleTransactions.reduce((sum, t) => sum + t.amount, 0);
    const addlExpenses = addlTransactions.filter(t => t.transactionType === 'Expense').reduce((sum, t) => sum + t.amount, 0);
    const addlIncomes = addlTransactions.filter(t => t.transactionType === 'Income').reduce((sum, t) => sum + t.amount, 0);

    const netPurchaseCost = totalPurchase + addlExpenses;
    const netSaleProceeds = totalSale + addlIncomes;

    // NEW: Use critical dates instead of transaction dates
    const acquisitionYear = getYear(acquisitionDate);
    const transferYear = getYear(transferDate);
    const ciiAcquisition = CII[acquisitionYear] || CII[Object.keys(CII)[0]];
    const ciiTransfer = CII[transferYear] || CII[Object.keys(CII)[Object.keys(CII).length - 1]];
    const indexedPurchaseCost = Math.round(netPurchaseCost * (ciiTransfer / ciiAcquisition));

    const oldRegimeGain = netSaleProceeds - indexedPurchaseCost;
    const oldRegimeTax = Math.max(0, Math.round(oldRegimeGain * 0.2));

    // New Regime: 10% without indexation
    const newRegimeGain = netSaleProceeds - netPurchaseCost;
    const newRegimeTax = Math.max(0, Math.round(newRegimeGain * 0.1));

    const savings = oldRegimeTax - newRegimeTax;

    return {
      oldRegime: oldRegimeTax,
      newRegime: newRegimeTax,
      savings: Math.max(0, savings)
    };
  };

  const clearCalculationMessage = () => {
    setCalculationMessage('');
  };

  const calculateTaxAndReturns = async () => {
    // Clear previous calculation message
    setCalculationMessage('');

    // Validate tax settings before calculation
    let hasErrors = false;

    if (tdsChecked && (!tdsPercent || parseFloat(tdsPercent) <= 0 || parseFloat(tdsPercent) > 100)) {
      setTdsError('Please enter a valid TDS percentage (1-100)');
      hasErrors = true;
    } else {
      setTdsError('');
    }

    if (section54Checked && (!section54Amount || parseFloat(section54Amount) <= 0)) {
      setSection54Error('Please enter a valid exemption amount');
      hasErrors = true;
    } else {
      setSection54Error('');
    }

    if (hasErrors) {
      setCalculationMessage('❌ Please fix the validation errors above before calculating tax.');
      return;
    }

    if (!hasErrors) {
      setIsCalculating(true);
      setCalculationMessage('🔄 Calculating tax... Please wait.');
      try {
        // NEW: Try backend calculation first, fallback to frontend
        if (user && acquisitionDate && transferDate) {
          try {
            const taxSettings = {
              tax_regime: taxRegime,
              holding_type: holdingType,
              owner_type: ownerType,
              apply_indexation: applyIndexation,
              section54_claimed: section54Checked,
              section54_amount: parseFloat(section54Amount) || 0,
              tds_deducted: tdsChecked,
              tds_percentage: parseFloat(tdsPercent) || 1,
              net_sale_proceeds: saleTransactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0) +
                addlTransactions.filter(t => t.transactionType === 'Income').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
              selling_expenses: addlTransactions.filter(t => t.transactionType === 'Expense').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
              improvement_cost: 0
            };

            console.log('Calling backend tax calculation with:', { acquisitionDate, transferDate, taxSettings });
            console.log('Current property ID:', apiService.getCurrentPropertyId());
            const backendResults = await apiService.calculateTax(acquisitionDate, transferDate, taxSettings);
            console.log('Backend results:', backendResults);

            // Update results with backend calculation
            setResults({
              capitalGain: backendResults.taxable_capital_gain,
              taxPayable: backendResults.tax_calculation.total_tax,
              netSaleProceeds: taxSettings.net_sale_proceeds,
              indexedPurchaseCost: backendResults.indexed_purchase_cost,
              holdingPeriod: backendResults.holding_period_months,
              gainType: backendResults.gain_type,
              breakdown: `Backend Calculation Results:\n\n` +
                `Acquisition Date: ${acquisitionDate}\n` +
                `Transfer Date: ${transferDate}\n` +
                `Holding Period: ${backendResults.holding_period_months} months\n` +
                `Gain Type: ${backendResults.gain_type}\n` +
                `Regime Applied: ${backendResults.regime_applied}\n` +
                `Indexed Purchase Cost: ₹${backendResults.indexed_purchase_cost.toLocaleString('en-IN')}\n` +
                `Capital Gain: ₹${backendResults.taxable_capital_gain.toLocaleString('en-IN')}\n` +
                `Tax Payable: ₹${backendResults.tax_calculation.total_tax.toLocaleString('en-IN')}\n` +
                `Net Tax Due: ₹${backendResults.net_tax_due.toLocaleString('en-IN')}\n\n` +
                `Indexation Details:\n${backendResults.indexation_info}`,
              taxRate: backendResults.tax_calculation.tax_rate.toFixed(1),
              surcharge: backendResults.tax_calculation.surcharge > 0 ? (backendResults.tax_calculation.surcharge / backendResults.tax_calculation.base_tax * 100).toFixed(0) : 0,
              educationCess: backendResults.tax_calculation.education_cess,
              netTaxDue: backendResults.net_tax_due,
              ownerType: taxSettings.owner_type,
              taxRegime: backendResults.regime_applied
            });

            setCalculationMessage(`✅ Backend tax calculation completed successfully! Capital Gain: ₹${backendResults.taxable_capital_gain.toLocaleString('en-IN')}`);
            return;

          } catch (backendError) {
            console.error('Backend calculation failed:', backendError);

            // Extract error message properly
            let errorMessage = 'Unknown error';
            if (typeof backendError === 'string') {
              errorMessage = backendError;
            } else if (backendError?.response?.data?.error) {
              errorMessage = backendError.response.data.error;
            } else if (backendError?.message) {
              errorMessage = backendError.message;
            } else if (backendError?.error) {
              errorMessage = backendError.error;
            }

            setCalculationMessage(`❌ Tax calculation failed: ${errorMessage}`);
            setIsCalculating(false);
            return;
          }
        } else {
          // Missing required data
          if (!user) {
            setCalculationMessage('❌ Please sign in to calculate tax.');
          } else if (!acquisitionDate) {
            setCalculationMessage('❌ Please set the acquisition date.');
          } else if (!transferDate) {
            setCalculationMessage('❌ Please set the transfer date.');
          }
          return;
        }

        // Frontend calculation as fallback
        calculateOverallResults();
        setCalculationMessage('⚠️ Backend calculation failed, using frontend calculation as fallback.');

        // Calculate regime comparison if owner is Individual/HUF
        if ((ownerType === 'Individual' || ownerType === 'HUF') && holdingType === 'LTCG') {
          const regimeComparison = calculateRegimeComparison();
          setCalculationMessage(prev => prev + ` | Regime Comparison: Old Regime: ₹${regimeComparison.oldRegime.toLocaleString('en-IN')}, New Regime: ₹${regimeComparison.newRegime.toLocaleString('en-IN')} | Savings: ₹${regimeComparison.savings.toLocaleString('en-IN')}`);
        }
      } finally {
        setIsCalculating(false);
        if (!calculationMessage.includes('✅') && !calculationMessage.includes('⚠️') && !calculationMessage.includes('❌')) {
          setCalculationMessage('✅ Tax calculation completed!');
        }
      }
    }
  };

  // Overall investment return analysis (XIRR/ROI)
  function getOverallResults() {
    // Gather all cash flows: purchases (outflow), sales (inflow), addl (in/out), with dates
    let cashflows = [];
    purchaseTransactions.forEach(t => cashflows.push({ amount: -parseFloat(t.amount), date: t.transaction_date || t.date, desc: t.description }));
    saleTransactions.forEach(t => cashflows.push({ amount: parseFloat(t.amount), date: t.transaction_date || t.date, desc: t.description }));
    addlTransactions.forEach(t => {
      const amount = parseFloat(t.amount);
      cashflows.push({ amount: t.transactionType === 'Expense' ? -amount : amount, date: t.transaction_date || t.date, desc: t.description });
    });

    // Debug logging
    console.log('Raw cashflows before filtering:', cashflows);

    cashflows = cashflows.filter(cf => cf.amount !== 0 && cf.date);
    console.log('Filtered cashflows:', cashflows);

    if (cashflows.length < 2) {
      console.log('Insufficient cashflows for XIRR calculation. Need at least 2, got:', cashflows.length);
      return null;
    }

    // Sort by date
    cashflows.sort((a, b) => new Date(a.date) - new Date(b.date));
    console.log('Sorted cashflows:', cashflows);

    // Validate dates
    const validCashflows = cashflows.filter(cf => {
      const date = new Date(cf.date);
      const isValid = !isNaN(date.getTime());
      if (!isValid) {
        console.error('Invalid date found:', cf.date, 'for transaction:', cf);
      }
      return isValid;
    });

    if (validCashflows.length !== cashflows.length) {
      console.warn('Some cashflows had invalid dates and were filtered out');
      cashflows = validCashflows;
    }

    if (cashflows.length < 2) {
      console.log('Insufficient valid cashflows after date validation');
      return null;
    }

    // XIRR (Pre-tax)
    let xirrValue = null;
    try {
      console.log('Attempting XIRR calculation with cashflows:', cashflows);
      xirrValue = xirr(cashflows);
      console.log('XIRR calculation result:', xirrValue);

      // Validate XIRR result
      if (xirrValue === null || isNaN(xirrValue) || !isFinite(xirrValue)) {
        console.warn('XIRR calculation returned invalid value:', xirrValue);
        xirrValue = null;
      }
    } catch (e) {
      console.error('XIRR calculation error:', e);
      xirrValue = null;
    }

    // Post-tax XIRR calculation
    let postTaxXirr = null;
    if (user && results && results.taxPayable !== undefined && results.taxPayable > 0) {
      try {
        // Create post-tax cashflows by reducing the final sale proceeds by tax payable
        const postTaxCashflows = [...cashflows];

        // Find the latest sale transaction to adjust with tax
        const latestSaleIndex = postTaxCashflows.reduce((latestIndex, cf, index) => {
          return (cf.amount > 0 && new Date(cf.date) >= new Date(postTaxCashflows[latestIndex]?.date || '1900-01-01')) ? index : latestIndex;
        }, -1);

        if (latestSaleIndex >= 0) {
          // Reduce the latest sale amount by the tax payable
          const originalAmount = postTaxCashflows[latestSaleIndex].amount;
          postTaxCashflows[latestSaleIndex] = {
            ...postTaxCashflows[latestSaleIndex],
            amount: originalAmount - results.taxPayable,
            desc: `${postTaxCashflows[latestSaleIndex].desc} (Post-tax: -₹${results.taxPayable.toLocaleString('en-IN')})`
          };

          console.log('Calculating post-tax XIRR with adjusted cashflows:', postTaxCashflows);
          postTaxXirr = xirr(postTaxCashflows);
          console.log('Post-tax XIRR calculation result:', postTaxXirr);

          // Validate post-tax XIRR result
          if (postTaxXirr === null || isNaN(postTaxXirr) || !isFinite(postTaxXirr)) {
            console.warn('Post-tax XIRR calculation returned invalid value:', postTaxXirr);
            postTaxXirr = null;
          }
        }
      } catch (e) {
        console.error('Post-tax XIRR calculation error:', e);
        postTaxXirr = null;
      }
    }

    // ROI  
    const totalInvested = cashflows.filter(cf => cf.amount < 0).reduce((sum, cf) => sum + Math.abs(parseFloat(cf.amount) || 0), 0);
    const totalReceived = cashflows.filter(cf => cf.amount > 0).reduce((sum, cf) => sum + (parseFloat(cf.amount) || 0), 0);
    const netProfit = totalReceived - totalInvested;
    const roi = totalInvested > 0 ? netProfit / totalInvested : null;

    // Holding period
    const holdingPeriod = monthsBetween(cashflows[0].date, cashflows[cashflows.length - 1].date);

    // Cash flow breakdown
    const cashFlowBreakdown = cashflows.map(cf => `${cf.date}: ${cf.amount < 0 ? '-' : '+'}₹${Math.abs(cf.amount).toLocaleString('en-IN')} (${cf.desc})`).join('\n');

    const finalResults = {
      xirr: xirrValue,
      postTaxXirr: postTaxXirr,
      roi,
      totalInvested,
      totalReceived,
      netProfit,
      holdingPeriod,
      cashFlowBreakdown,
      // Include tax results for reference
      taxResults: results
    };

    console.log('Final results with post-tax XIRR:', finalResults);
    return finalResults;
  }

  // Calculate overall results with proper dependency on tax results
  const overallResults = useMemo(() => {
    return getOverallResults();
  }, [purchaseTransactions, saleTransactions, addlTransactions, results, user]);

  // Update parent component when overall results change
  useEffect(() => {
    if (onOverallResultsChange) {
      onOverallResultsChange(overallResults);
    }
  }, [overallResults, onOverallResultsChange]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'purchase':
        return <PurchaseTab
          transactions={purchaseTransactions}
          setTransactions={setPurchaseTransactions}
          onAddTransaction={addPurchaseTransaction}
          onRemoveTransaction={removeTransaction}
          user={user}
        />;
      case 'sale':
        return <SaleTab
          transactions={saleTransactions}
          setTransactions={setSaleTransactions}
          onAddTransaction={addSaleTransaction}
          onRemoveTransaction={removeTransaction}
          user={user}
        />;
      case 'expenses':
        return <ExpensesTab
          transactions={addlTransactions}
          setTransactions={setAddlTransactions}
          onAddTransaction={addAdditionalTransaction}
          onRemoveTransaction={removeTransaction}
          user={user}
        />;
      case 'tax':
        return <TaxTab
          holdingType={holdingType}
          setHoldingType={setHoldingType}
          ownerType={ownerType}
          setOwnerType={setOwnerType}
          taxRegime={taxRegime}
          setTaxRegime={setTaxRegime}
          applyIndexation={applyIndexation}
          setApplyIndexation={setApplyIndexation}
          tdsChecked={tdsChecked}
          setTdsChecked={setTdsChecked}
          tdsPercent={tdsPercent}
          setTdsPercent={setTdsPercent}
          section54Checked={section54Checked}
          setSection54Checked={setSection54Checked}
          section54Amount={section54Amount}
          currentProperty={currentProperty}
          setSection54Amount={setSection54Amount}
          section54Error={section54Error}
          setSection54Error={setSection54Error}
          tdsError={tdsError}
          setTdsError={setTdsError}
          onCalculateTax={calculateTaxAndReturns}
          isCalculating={isCalculating}
          calculationMessage={calculationMessage}
          clearCalculationMessage={clearCalculationMessage}
          purchaseTransactions={purchaseTransactions}
          saleTransactions={saleTransactions}
          addlTransactions={addlTransactions}
          showAdvancedOptions={showAdvancedOptions}
          setShowAdvancedOptions={setShowAdvancedOptions}
          showTaxPreview={showTaxPreview}
          setShowTaxPreview={setShowTaxPreview}
          // NEW: Critical dates for tax calculation
          acquisitionDate={acquisitionDate}
          setAcquisitionDate={setAcquisitionDate}
          transferDate={transferDate}
          setTransferDate={setTransferDate}
          user={user}
        />;
      case 'analytics':
        return <AnalyticsDashboard
          purchaseTransactions={purchaseTransactions}
          saleTransactions={saleTransactions}
          additionalTransactions={addlTransactions}
          overallResults={overallResults}
        />;
      default:
        return null;
    }
  };

  // Only show tax summary in Tax tab
  const showTaxSummary = activeTab === 'tax';

  return (
    <div className="tabs-section">
      <div className="tab-navigation">
        {tabList.map(tab => {
          // FREE LAUNCH: All tabs are accessible
          const isPremium = false; // tab.premium; // Commented out for free launch
          const isLocked = false; // isPremium && !subscription?.is_premium; // Commented out for free launch

          return (
            <button
              key={tab.key}
              className={`tab-button${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => {
                // FREE LAUNCH: No upgrade prompts
                // if (isLocked) {
                //   onUpgradeClick();
                // } else {
                setActiveTab(tab.key);
                // }
              }}
              style={{
                position: 'relative',
                opacity: 1 // isLocked ? 0.7 : 1 // Always full opacity for free launch
              }}
            >
              {tab.label}
              {/* FREE LAUNCH: No lock icons
              {isLocked && (
                <span style={{ marginLeft: '6px', fontSize: '0.9rem' }}>🔒</span>
              )}
              */}
            </button>
          );
        })}
      </div>
      {renderTabContent()}
      {showTaxSummary && <TaxSummary results={results} />}
    </div>
  );
};

export default Tabs;